import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, RefreshCw, CheckCircle, XCircle, AlertCircle, 
  Clock, FileText, ArrowLeft, Settings, Play, History,
  Save, Eye, EyeOff, TestTube, Server, Key, ToggleLeft, ToggleRight
} from 'lucide-react';
import { LOGO_DARK_BG } from '../config/branding';

const AdminEmailImportPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    imap_server: '',
    imap_port: 993,
    email: 'import@dein-gutachten-direkt.de',
    password: '',
    enabled: true
  });
  
  const adminToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [adminToken, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load status
      const statusRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/status`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
        // Pre-fill form with current config
        setConfigForm(prev => ({
          ...prev,
          imap_server: statusData.imap_server !== 'Nicht konfiguriert' ? statusData.imap_server : '',
          imap_port: statusData.imap_port || 993,
          email: statusData.email || 'import@dein-gutachten-direkt.de',
          enabled: statusData.enabled || false
        }));
      }

      // Load history
      const historyRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/history?limit=30`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!configForm.imap_server || !configForm.email || !configForm.password) {
      alert('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/config`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configForm)
      });
      const result = await res.json();
      if (result.success) {
        alert('Konfiguration gespeichert!');
        setShowConfig(false);
        loadData();
      } else {
        alert('Fehler: ' + (result.error || 'Unbekannter Fehler'));
      }
    } catch (error) {
      alert('Fehler beim Speichern: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // First save the config
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/config`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configForm)
      });
      
      // Then test connection
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const result = await res.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const triggerImport = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-import/trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const result = await res.json();
      alert(result.success 
        ? `Import abgeschlossen: ${result.processed || 0} verarbeitet, ${result.errors || 0} Fehler`
        : `Import fehlgeschlagen: ${result.error}`
      );
      loadData();
    } catch (error) {
      alert('Fehler beim Import: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'no_case_found':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (statusType) => {
    const styles = {
      success: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      no_case_found: 'bg-amber-100 text-amber-800'
    };
    const labels = {
      success: 'Erfolgreich',
      error: 'Fehler',
      no_case_found: 'Fall nicht gefunden'
    };
    return (
      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${styles[statusType] || 'bg-gray-100 text-gray-800'}`}>
        {labels[statusType] || statusType}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Common IMAP servers
  const imapServers = [
    { name: 'IONOS', server: 'imap.ionos.de' },
    { name: 'Google Workspace', server: 'imap.gmail.com' },
    { name: 'Microsoft 365', server: 'outlook.office365.com' },
    { name: 'Strato', server: 'imap.strato.de' },
    { name: '1&1', server: 'imap.1und1.de' },
    { name: 'Zoho', server: 'imap.zoho.eu' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img src={LOGO_DARK_BG} alt="Logo" className="h-8" />
              <div className="h-6 w-px bg-slate-700" />
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                E-Mail Import (Ultra Expert)
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowConfig(true)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                data-testid="config-btn"
              >
                <Settings className="w-4 h-4" />
                Konfiguration
              </button>
              <button
                onClick={triggerImport}
                disabled={importing || !status?.configured}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {importing ? 'Importiere...' : 'Jetzt importieren'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-500">Lade Daten...</p>
          </div>
        ) : (
          <>
            {/* Status Card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-400" />
                Konfigurationsstatus
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    {status?.configured ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-amber-500" />
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {status?.configured ? 'Aktiv' : 'Nicht konfiguriert'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-8 h-8 text-blue-500" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">E-Mail-Adresse</p>
                      <p className="font-bold text-gray-900 dark:text-white text-sm">
                        {status?.email || 'Nicht konfiguriert'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-8 h-8 text-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Automatischer Import</p>
                      <p className="font-bold text-gray-900 dark:text-white">Alle 5 Minuten</p>
                    </div>
                  </div>
                </div>
              </div>

              {!status?.configured && (
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-amber-800 dark:text-amber-200 text-sm">
                    <strong>Hinweis:</strong> IMAP-Zugangsdaten müssen in der Backend-Konfiguration hinterlegt werden. 
                    Klicken Sie auf "Konfiguration" für Details.
                  </p>
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 mb-8 text-white">
              <h2 className="text-lg font-bold mb-4">So funktioniert der Import</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-2">1</div>
                  <p className="text-sm">Ultra Expert sendet Gutachten an<br/><strong>{status?.email}</strong></p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-2">2</div>
                  <p className="text-sm">System erkennt Kennzeichen im Betreff</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-2">3</div>
                  <p className="text-sm">PDFs werden automatisch dem Fall zugeordnet</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-2">4</div>
                  <p className="text-sm">Partner & Kunde werden per E-Mail benachrichtigt</p>
                </div>
              </div>
            </div>

            {/* Import History */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-400" />
                  Import-Verlauf
                </h2>
                <button
                  onClick={loadData}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>

              {history.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Noch keine Imports durchgeführt</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Zeit</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Kennzeichen</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Betreff</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Anhänge</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {history.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                            {formatDate(item.timestamp)}
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-mono font-bold text-gray-900 dark:text-white">
                              {item.license_plate || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate">
                            {item.email_subject}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                {item.attachments_count || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(item.status)}
                              {getStatusBadge(item.status)}
                            </div>
                            {item.error_message && (
                              <p className="text-xs text-red-500 mt-1">{item.error_message}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Config Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-indigo-600">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                E-Mail Import Konfiguration
              </h2>
              <p className="text-blue-100 text-sm mt-1">IMAP-Zugangsdaten für import@dein-gutachten-direkt.de</p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-5">
              {/* IMAP Server */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Server className="w-4 h-4 inline mr-2" />
                  IMAP Server
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={configForm.imap_server}
                    onChange={(e) => setConfigForm({ ...configForm, imap_server: e.target.value })}
                    placeholder="imap.ionos.de"
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="imap-server-input"
                  />
                  <select
                    onChange={(e) => setConfigForm({ ...configForm, imap_server: e.target.value })}
                    className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Anbieter wählen...</option>
                    {imapServers.map(s => (
                      <option key={s.server} value={s.server}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Port */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Port (Standard: 993)
                </label>
                <input
                  type="number"
                  value={configForm.imap_port}
                  onChange={(e) => setConfigForm({ ...configForm, imap_port: parseInt(e.target.value) || 993 })}
                  className="w-32 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={configForm.email}
                  onChange={(e) => setConfigForm({ ...configForm, email: e.target.value })}
                  placeholder="import@dein-gutachten-direkt.de"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="email-input"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Key className="w-4 h-4 inline mr-2" />
                  Passwort
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={configForm.password}
                    onChange={(e) => setConfigForm({ ...configForm, password: e.target.value })}
                    placeholder="E-Mail Passwort eingeben"
                    className="w-full px-4 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Enabled Toggle */}
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Automatischer Import</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Alle 5 Minuten prüfen</p>
                </div>
                <button
                  onClick={() => setConfigForm({ ...configForm, enabled: !configForm.enabled })}
                  className={`w-14 h-8 rounded-full transition-colors flex items-center px-1 ${
                    configForm.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  data-testid="enabled-toggle"
                >
                  <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                    configForm.enabled ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-xl border-2 ${
                  testResult.success 
                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                    : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                      {testResult.success ? testResult.message : testResult.error}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
              <button
                onClick={testConnection}
                disabled={testing || !configForm.imap_server || !configForm.password}
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 disabled:bg-gray-100 text-amber-800 disabled:text-gray-400 rounded-xl font-semibold flex items-center gap-2 transition-colors"
                data-testid="test-connection-btn"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4" />
                )}
                {testing ? 'Teste...' : 'Verbindung testen'}
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfig(false)}
                  className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl font-semibold transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={saveConfig}
                  disabled={saving || !configForm.imap_server || !configForm.password}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-xl font-semibold flex items-center gap-2 transition-colors"
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailImportPage;
